import SorenCashCite from './SorenCashCite.tsx'
import AriaChatMarkdown from '../../components/aria/AriaChatMarkdown'
import type { ForecastLine, ForecastVersion } from '../../api/cashReport'
import type { ShapedCashAnswer } from './shapeCashAnswer.ts'
import { formatCalendarMonthDay } from '../../lib/localDate.ts'

type Props = {
  shaped: ShapedCashAnswer
  version: ForecastVersion
  pageVersionId?: string | null
  preparedText?: string | null
  onOpenReport: () => void
  onRefresh?: () => void
  onOpenSource?: (href: string) => void
}

export default function SorenCashEngineAnswer({
  shaped,
  version,
  pageVersionId,
  preparedText,
  onOpenReport,
  onRefresh,
  onOpenSource,
}: Props) {
  const lines = (shaped.sourceLines != null
    ? shaped.sourceLines
    : (version.payload?.lines || [])) as ForecastLine[]
  const open = shaped.mode === 'open'
  return (
    <div className="soren-engine-answer" data-testid="soren-cash-answer">
      {open ? (
        <>
          <p className="prose" data-testid="soren-cash-lead">{shaped.lead}</p>
          <ul className="soren-fact-list" data-testid="soren-cash-open-lines">
            <li data-testid="soren-cash-due-out"><span>Due out</span><strong className="money">{shaped.dueOut}</strong></li>
            <li data-testid="soren-cash-week0"><span>This week closes at</span><strong className="money">{shaped.week0Close}</strong></li>
            <li data-testid="soren-cash-lowest">
              <span>Lowest point</span>
              <strong className="money">{shaped.lowest}{shaped.lowestWeek ? ` in the week of ${formatCalendarMonthDay(shaped.lowestWeek)}` : ''}</strong>
            </li>
            <li data-testid="soren-cash-confidence"><span>Confidence</span><strong>{shaped.confidence}</strong></li>
          </ul>
          {shaped.help && shaped.help.length ? (
            <div className="soren-cash-help" data-testid="soren-cash-help">
              <h4>What would help most</h4>
              <ol>
                {shaped.help.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ol>
            </div>
          ) : null}
        </>
      ) : (
        <>
          {shaped.reason ? (
            <div className="soren-fact-reason" data-testid="soren-fact-reason">
              <AriaChatMarkdown text={shaped.reason} />
            </div>
          ) : null}
          <p className="prose soren-yesno" data-testid="soren-cash-figures">
            <b>{shaped.yesNo}</b>
          </p>
          {shaped.offerFollowUp ? (
            <p className="soren-followup-offer" data-testid="soren-followup-offer">I can draft a follow-up</p>
          ) : null}
          <ul className="soren-fact-list">
            {shaped.amount ? <li><span>Amount</span><strong className="money">{shaped.amount}</strong></li> : null}
            {shaped.date ? <li><span>When</span><strong>{shaped.date}</strong></li> : null}
          </ul>
        </>
      )}
      <SorenCashCite
        versionId={shaped.version_id}
        computedAt={shaped.computed_at}
        pageVersionId={pageVersionId}
        confidence={shaped.confidence}
        sourceLines={lines}
        onOpenReport={onOpenReport}
        onRefresh={onRefresh}
        onOpenSource={onOpenSource}
      />
      {preparedText ? (
        <div className="soren-prepared" data-testid="soren-prepared-copy">
          <pre>{preparedText}</pre>
          <button
            type="button"
            className="cr-btn"
            onClick={() => void navigator.clipboard.writeText(preparedText)}
          >
            Copy
          </button>
        </div>
      ) : null}
    </div>
  )
}
